
import * as Core from '@skylab-shared/core';
import { RepositoryResponseConflictCodes, ResponseConflictObject } from '@skylab-shared/core';
import { FiscalDeviceModel, FiscalDevicePaymentMethodsParametersMappingModel, FiscalDeviceVatRatesParametersMappingModel, FiscalDeviceVatRatesParametersModel } from '@skylab-shared/dataModel';
import { sameValues } from '@skylab-shared/utils';
import { propertyChangedJoinPointFilter as propertyJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { DuplicatePaymentMethodError } from './violations/duplicate-payment-method-error';
import { DuplicateVatRateError } from './violations/duplicate-vat-rate-error';
import { TechnicalZeroError } from './violations/technical-zero-error';
import { VatRateUsedWithTechnicalZeroError } from './violations/vat-rate-used-with-technical-zero-error';


@Core.BO({ modelType: FiscalDeviceModel })
export class FiscalDeviceBO extends Core.BusinessObjectBase<FiscalDeviceModel> {
    private previousDefaultValue: boolean;

    public constructor(
        weaver: Core.Weaver,
    ) {
        super(weaver);
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        this.registerValidationRules();
        this.registerReadonlyRules();

        if (this.model.isPersistent) {
            this.previousDefaultValue = this.model.default;
        }
    }

    protected override handleConflictSaveResponse(conflict: ResponseConflictObject<RepositoryResponseConflictCodes>) {
        if ((conflict.code === RepositoryResponseConflictCodes.UniqueConstraintViolation) && sameValues(conflict.objectProperties!, ['name'])) {
            this.addExternalRuleViolations({
                [this.model.sun]: [{
                    property: 'name',
                    violation: new Core.NonUniqueModelDataError('business.fiscalization.validation.name.server-rule-violations.non-unique-model-data-error')
                }]
            });
        }
    }

    private registerValidationRules(): void {
        this.registerPaymentMethodRule();
        this.registerTechnicalZeroRule();
        this.registerVatRateRules();
        this.registerReadonlyRules();
    }

    private registerReadonlyRules(): void {
        this.readonly.addRule(
            [[FiscalDeviceModel, 'name']],
            ctx => ctx.source.isPersistent
        );
        this.readonly.addRule(
            [[FiscalDeviceModel, 'default']],
            _ => this.previousDefaultValue
        );
    }

    private registerTechnicalZeroRule(): void {
        this.validation.addRule(
            [[FiscalDeviceVatRatesParametersModel, 'technicalZero']],
            ctx => {
                if (ctx.bo.model.vatRatesParameters?.technicalZero === null) {
                    return true;
                }
                const mappedTechnicalZero = ctx.bo.model.vatRatesParameters?.mappings.find(m => m.mapped === ctx.bo.model.vatRatesParameters?.technicalZero);
                return mappedTechnicalZero
                    ? mappedTechnicalZero.value === 0
                    : true;
            },
            TechnicalZeroError);

        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, propertyJoinPointFilter(this)(FiscalDeviceVatRatesParametersMappingModel, ['value'])(
            () => {
                this.validation.queueValidation([FiscalDeviceVatRatesParametersModel, 'technicalZero']);
            }
        )));
    }

    private registerPaymentMethodRule(): void {
        this.validation.addRule(
            [[FiscalDevicePaymentMethodsParametersMappingModel, 'paymentMethodId']],
            ctx => {
                const filteredMappings = ctx.bo.model.paymentMethodsParameters?.mappings.filter(m => {
                    if (ctx.source.paymentMethodId !== null) {
                        if (ctx.source.sun !== m.sun) {
                            return m.paymentMethodId === ctx.source.paymentMethodId;
                        }
                    }
                    return false;
                });
                return filteredMappings ? filteredMappings.length <= 0 : true;
            },
            DuplicatePaymentMethodError);
    }

    private registerVatRateRules(): void {
        this.validation.addRule(
            [[FiscalDeviceVatRatesParametersMappingModel, 'vatRateId']],
            ctx => {
                const filteredMappings = ctx.bo.model.vatRatesParameters?.mappings.filter(m => {
                    if (ctx.source.vatRateId !== null) {
                        if (ctx.source.sun !== m.sun) {
                            return m.vatRateId === ctx.source.vatRateId;
                        }
                    }
                    return false;
                });
                return filteredMappings ? filteredMappings.length <= 0 : true;
            },
            DuplicateVatRateError
        );

        this.validation.addRule(
            [[FiscalDeviceVatRatesParametersMappingModel, 'vatRateId']],
            ctx => {
                if (ctx.bo.model.vatRatesParameters?.technicalZero && ctx.bo.model.vatRatesParameters?.technicalZero === ctx.source.mapped) {
                    return ctx.source.vatRateId === null;
                }
                return true;
            },
            VatRateUsedWithTechnicalZeroError
        );

        this.registerListening(this.weaver.register(Core.PropertyChangedJoinPoint, propertyJoinPointFilter(this)(FiscalDeviceVatRatesParametersModel, ['technicalZero'])(
            () => {
                this.validation.queueValidation([FiscalDeviceVatRatesParametersMappingModel, 'vatRateId']);
            }
        )));
    }
}
